import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface UIContextType {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  setFullscreen: (value: boolean) => void;
  isDesktopView: boolean;
  toggleDesktopView: () => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDesktopView, setIsDesktopView] = useState(() => {
    return localStorage.getItem('isDesktopView') === 'true';
  });

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const updateViewport = (desktop: boolean) => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      if (desktop) {
        // Force desktop view by setting a fixed width
        viewport.setAttribute('content', 'width=1200, initial-scale=0.1');
      } else {
        // Return to mobile view
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    }
  };

  const toggleDesktopView = () => {
    setIsDesktopView(prev => {
      const newValue = !prev;
      localStorage.setItem('isDesktopView', String(newValue));
      updateViewport(newValue);
      return newValue;
    });
  };

  // Sync viewport on mount and when isDesktopView changes
  useEffect(() => {
    updateViewport(isDesktopView);
  }, [isDesktopView]);

  // Adjust sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isSidebarOpen) {
        setIsSidebarOpen(false);
      } else if (window.innerWidth >= 1024 && !isSidebarOpen && !isFullscreen) {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen, isFullscreen]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
      setIsSidebarOpen(false); // Hide sidebar when entering fullscreen
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
      setIsSidebarOpen(true); // Show sidebar when exiting fullscreen
    }
  };

  const setFullscreen = (value: boolean) => {
    setIsFullscreen(value);
    if (value) {
      setIsSidebarOpen(false); // Hide sidebar when entering fullscreen programmatically
    } else {
      setIsSidebarOpen(true); // Show sidebar when exiting fullscreen programmatically
    }
  };

  // Listen for native fullscreen changes (e.g., user pressing F11)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const currentFullscreen = !!document.fullscreenElement;
      setIsFullscreen(currentFullscreen);
      // Only toggle sidebar if it's not already explicitly managed by the component
      // For now, always show sidebar on exit, hide on enter
      setIsSidebarOpen(!currentFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <UIContext.Provider value={{
      isSidebarOpen,
      toggleSidebar,
      isFullscreen,
      toggleFullscreen,
      setFullscreen,
      isDesktopView,
      toggleDesktopView
    }}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
